import { NavLink } from "react-router-dom";
import { useCurrentUser } from "@/lib/auth";
import { canSee, type PageKey } from "@/lib/pages";
import { cn } from "@/lib/utils";

// Вкладки-«секции» одной страницы, каждая из которых остаётся отдельным
// РОУТОМ. Так сохраняются старые ссылки (уведомления в Slack/TG ведут на
// /health, /crawling, …), гейты RBAC (у каждого пути свой PageGuard) и история
// браузера, а слева в меню вместо четырёх пунктов остаётся один.
//
// Вкладку, закрытую ролью, просто не показываем — как и пункт меню.

export type PageTab = { page: PageKey; label: string; to: string };

export const SITES_TABS: PageTab[] = [
  { page: "sites", label: "Сайты", to: "/sites" },
  { page: "sweep", label: "Сводная", to: "/sweep" },
  { page: "compare", label: "Сравнение", to: "/compare" },
  { page: "markers", label: "Метки", to: "/markers" },
  { page: "sites", label: "Изменения", to: "/changes" },
];

export const MONITORING_TABS: PageTab[] = [
  { page: "health", label: "Здоровье", to: "/health" },
  { page: "crawling", label: "Краулинг", to: "/crawling" },
  { page: "tracking", label: "Отслеживание", to: "/tracking" },
  { page: "ads", label: "Реклама", to: "/ads" },
];

// «Офферы» разложены на вкладки внутри самой страницы (см. pages/Offers.tsx);
// здесь — только то, что нужно меню: какие роуты страница накрывает.
export const OFFERS_TABS: PageTab[] = [
  { page: "offers", label: "Офферы", to: "/offers" },
  { page: "domains", label: "Домены", to: "/domains" },
];

// AI-аналитика: отчёт-статья (analysis) и ежедневные вердикты Claude по
// сайтам (portfolio, репо observer-daily). Вердикты живут под /analysis/…,
// старые /verdicts/* редиректят (см. App.tsx).
export const ANALYSIS_TABS: PageTab[] = [
  { page: "analysis", label: "Аналитика", to: "/analysis" },
  { page: "portfolio", label: "Вердикты", to: "/analysis/verdicts" },
];

export const SETTINGS_TABS: PageTab[] = [
  { page: "settings", label: "Настройки", to: "/settings" },
  { page: "users", label: "Пользователи", to: "/users" },
  { page: "logs", label: "Логи", to: "/logs" },
  { page: "backups", label: "Бэкапы", to: "/backups" },
];

function PageTabs({ tabs }: { tabs: PageTab[] }) {
  const { user } = useCurrentUser();
  const visible = tabs.filter((t) => canSee(user, t.page));
  // Одна доступная вкладка — это не вкладки, а просто страница.
  if (visible.length < 2) return null;
  return (
    <div className="flex gap-1 border-b border-ink-200/70 dark:border-ink-800/70 -mb-px overflow-x-auto scrollbar-none">
      {visible.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          className={({ isActive }) =>
            cn(
              "inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 shrink-0 whitespace-nowrap",
              isActive
                ? "border-brand-500 text-brand-600 dark:text-brand-300"
                : "border-transparent text-ink-500 dark:text-ink-400 hover:text-ink-700 dark:hover:text-ink-200",
            )
          }
        >
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}

// Обёртка роута: панель вкладок сверху, сама страница — под ней (свой
// заголовок и тулбар страницы не трогаем).
export default function TabbedPage({
  tabs,
  children,
}: {
  tabs: PageTab[];
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4">
      <PageTabs tabs={tabs} />
      {children}
    </div>
  );
}
